<script setup lang="ts">
import type { TransformedCartLine } from '~/server/utils/cartTypes'

const { cartLines, cartCount, cartTotal, checkoutUrl, updateQuantity, removeItem } = useCart()

const promoCode = ref('')
const promoApplied = ref(false)

async function decreaseQty(line: TransformedCartLine) {
  if (line.quantity <= 1) {
    await removeItem(line.id)
  }
  else {
    await updateQuantity(line.id, line.quantity - 1)
  }
}

async function increaseQty(line: TransformedCartLine) {
  await updateQuantity(line.id, line.quantity + 1)
}

function applyPromo() {
  if (promoCode.value.trim()) promoApplied.value = true
}
</script>

<template>
  <div class="bg-page flex flex-col items-start relative md:max-w-7xl w-full mx-auto">
    <!-- Page Header -->
    <div class="flex flex-col gap-2 items-start pb-8 pt-24 px-4 md:px-20 w-full ">
      <p class="font-sans text-[13px] text-muted">Home &nbsp;/&nbsp; Goods &nbsp;/&nbsp; Cart</p>
      <p class="font-serif font-semibold text-4xl md:text-[42px] text-dark">Your Cart</p>
      <p class="font-sans text-[15px] text-secondary">{{ cartLines.length }} item{{ cartLines.length !== 1 ? 's' : '' }}</p>
    </div>

    <!-- Cart Body -->
    <div class="flex flex-col md:flex-row gap-12 items-start pb-24 pt-2 px-4 md:px-20 w-full md:max-w-7xl mx-auto">
      <!-- Cart Items -->
      <div class="flex flex-col flex-1 items-start w-full">
        <template v-if="cartLines.length === 0">
          <p class="font-sans text-[15px] text-secondary py-8">Your cart is empty.</p>
        </template>
        <template v-else>
          <div
            v-for="(line, index) in cartLines"
            :key="line.id"
            class="flex gap-6 items-center py-4 w-full"
            :class="{ 'border-t border-stroke': index > 0 }"
          >
            <!-- Product Image -->
            <div class="rounded-lg shrink-0 size-24 bg-raised overflow-hidden flex items-center justify-center">
              <img v-if="line.image" :src="line.image" :alt="line.productTitle" class="object-cover size-full rounded-lg" />
              <div v-else class="size-full bg-raised rounded-lg" />
            </div>
            <!-- Info -->
            <div class="flex flex-1 flex-col gap-1.5 items-start min-w-0">
              <p class="font-sans font-medium text-base text-dark truncate w-full">{{ line.productTitle }}</p>
              <p class="font-sans text-[13px] text-muted">{{ line.variantTitle }}</p>
              <!-- Qty Control -->
              <div class="border border-stroke flex gap-3.5 items-center px-2.5 py-1.5 rounded-md shrink-0">
                <button
                  class="font-sans font-medium text-lg text-secondary leading-none"
                  @click="decreaseQty(line)"
                >−</button>
                <span class="font-sans font-medium text-[15px] text-dark w-3 text-center">{{ line.quantity }}</span>
                <button
                  class="font-sans font-medium text-lg text-secondary leading-none"
                  @click="increaseQty(line)"
                >+</button>
              </div>
            </div>
            <!-- Price -->
            <div class="flex flex-col gap-2 items-end shrink-0 w-[100px]">
              <p class="font-sans font-semibold text-lg text-dark">{{ line.price }}</p>
              <button
                class="font-sans text-xs text-muted hover:text-secondary transition-colors"
                @click="removeItem(line.id)"
              >Remove</button>
            </div>
          </div>
        </template>
      </div>

      <!-- Order Summary -->
      <div class="bg-surface flex flex-col items-start px-7 py-8 rounded-xl shrink-0 w-full md:w-[368px]">
        <p class="font-serif font-semibold text-[22px] text-dark">Order Summary</p>
        <div class="h-6" />

        <!-- Shipping -->
        <div class="flex items-center justify-between py-4 text-sm w-full">
          <p class="font-sans text-secondary">Shipping</p>
          <p class="font-sans font-medium text-brand">Free</p>
        </div>

        <!-- Divider -->
        <div class="bg-stroke h-px w-full" />
        <div class="h-4" />

        <!-- Total -->
        <div class="flex font-sans font-semibold items-center justify-between text-dark w-full">
          <p class="text-lg">Total</p>
          <p class="text-[22px]">{{ cartTotal }}</p>
        </div>
        <div class="h-7" />

        <!-- Promo Code -->
        <p class="font-sans font-medium text-[13px] text-secondary">Promo Code</p>
        <div class="h-2" />
        <div class="flex gap-2 h-11 items-center w-full">
          <input
            v-model="promoCode"
            type="text"
            placeholder="Enter code"
            class="bg-surface border border-stroke flex-1 h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors min-w-0"
          />
          <button
            class="bg-raised h-11 font-sans font-medium text-[13px] text-dark px-4 rounded-lg hover:bg-stroke transition-colors shrink-0"
            @click="applyPromo"
          >Apply</button>
        </div>
        <div class="h-6" />

        <!-- Proceed to Checkout -->
        <NuxtLink
          to="/checkout"
          class="bg-primary flex gap-1 items-center justify-center px-3 py-2 w-full"
          :class="{ 'opacity-50 pointer-events-none': cartLines.length === 0 }"
        >
          <span class="font-label text-base text-white leading-6">Proceed to Checkout</span>
          <svg class="size-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </NuxtLink>
        <div class="h-3.5" />
        <p class="font-sans text-xs text-muted text-center w-full">🔒&nbsp; Secure checkout</p>
      </div>
    </div>

  </div>
</template>
